import * as React from 'react';
import { styled } from 'styled-components';
import type { CheckboxState, CheckedChangeEvent } from '@sunwebgroup/ui-components';
import { SpaceBetween, tk, Checkbox, Typography, DropdownMultiselectContext } from '@sunwebgroup/ui-components';

const Wrapper = styled(SpaceBetween)`
  margin-bottom: ${tk('spacing_md')};
`;

const CheckboxWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:last-of-type {
    margin-bottom: 0;
  }

  > ${Typography} {
    color: ${tk('color_semantic_text_subdued')};
  }
`;

export const OptionGroup = ({
  id,
  value,
  options,
}: {
  id: string;
  value: string;
  options: { id: string; value: string; trailing?: string }[];
  trailing?: string | undefined;
}) => {
  const { selectedValues, removeOptions, addOptions } = React.useContext(DropdownMultiselectContext);

  const [isSelected, setIsSelected] = React.useState<CheckboxState>(false);
  const [halfSelected, setHalfSelected] = React.useState<boolean>(false);

  const myGroupIds = React.useMemo(() => options.map((option) => option.id), [options]);
  const containsAllIds = React.useMemo(
    () => myGroupIds.every((optionId) => selectedValues.includes(optionId)),
    [selectedValues, myGroupIds]
  );

  const containsSomeIds = React.useMemo(
    () => myGroupIds.some((optionId) => selectedValues.includes(optionId)),
    [selectedValues, myGroupIds]
  );

  React.useEffect(() => {
    if (containsAllIds) {
      setIsSelected(true);
      setHalfSelected(false);
    } else if (containsSomeIds) {
      setIsSelected('indeterminate');
      setHalfSelected(true);
    } else {
      setIsSelected(false);
      setHalfSelected(false);
    }
  }, [selectedValues, containsAllIds, containsSomeIds]);

  const onCheckedChangeHandler = ({ checked }: CheckedChangeEvent) => {
    setIsSelected(checked);
    setHalfSelected(false);
    if (checked) {
      const notCheckedOptionsIds = options
        .filter((option) => !selectedValues.includes(option.id))
        .map((option) => option.id);
      addOptions(notCheckedOptionsIds);
    } else {
      const checkedOptionsIds = options
        .filter((option) => selectedValues.includes(option.id))
        .map((option) => option.id);
      removeOptions(checkedOptionsIds);
    }
  };

  return (
    <Wrapper direction="y" spacing={tk('spacing_md')}>
      <CheckboxWrapper>
        <Checkbox
          value={id}
          label={value}
          checked={isSelected}
          halfChecked={halfSelected}
          onCheckedChange={onCheckedChangeHandler}
        />
      </CheckboxWrapper>
      {options.map((child) => {
        return <Option key={child.id} id={child.id} value={child.value} trailing={child.trailing} />;
      })}
    </Wrapper>
  );
};

interface OptionProps {
  id: string;
  value: string;
  trailing?: string;
}
const OptionWrapper = styled.div`
  margin-left: ${tk('spacing_lg')};
`;

const Option = ({ id, value, trailing }: OptionProps) => {
  const { selectedValues, removeOption, addOption } = React.useContext(DropdownMultiselectContext);

  const [isSelected, setIsSelected] = React.useState<CheckboxState>(false);
  React.useEffect(() => {
    setIsSelected(selectedValues.includes(id));
  }, [selectedValues, id]);

  const onCheckedChangeHandler = (event: CheckedChangeEvent) => {
    if (event.checked) {
      addOption(id);
    } else {
      removeOption(id);
    }
  };

  return (
    <OptionWrapper>
      <CheckboxWrapper>
        <Checkbox value={id} onCheckedChange={onCheckedChangeHandler} label={value} checked={isSelected} />
        {trailing && <Typography $variant="body-md">{trailing}</Typography>}
      </CheckboxWrapper>
    </OptionWrapper>
  );
};
